import { FunctionComponent, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { isAxiosError } from 'axios';

import { Headline, Gutter, Div, Loader, Title } from '@gaiads/telia-react-component-library';
import {
  AssetsCard,
  COUNTRIES,
  ProfileCard,
  ProfileCardTranslations,
  UserRightsAndLimitsCard,
  UserRightsAndLimitsCardTranslations,
  InvoicingAccountCard,
  InvoicingAccountCardTranslations,
  AssetsCardTranslations,
} from '@cdaas/reusable-components-daas';
import {
  ConflictErrorErrorEnum,
  InvoicingAccountOptionResponseContents,
  UpdateUserRequest,
  UserAssetResponse,
} from '@cdaas/api-clients/dist/asset-user-openapi';

import { getAUser, getInvoicingAccounts, getUsersAllAssets, updateUser } from '../../../utils';
import { User } from '../../../types';

const UserAdmin: FunctionComponent = () => {
  const params = useParams<{ id: string; usergroupid: string }>();
  const [user, setUser] = useState<User>({
    name: '',
    id: '',
    email: '',
    user_roles: { effective: [], user: [], user_group: [] },
    allowed_procurements: [],
    allow_access_without_bank_id: false,
    inherited_invoicing_account: {
      name: '',
    },
  });
  const [organizationInvoicingAccounts, setOrganizationInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>([]);
  const [assets, setAssets] = useState<UserAssetResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchUserError, setFetchUserError] = useState<boolean>(false);

  const { t: tUser } = useTranslation('editOrganization');
  const { t: tGlobal } = useTranslation('globalComponents');
  const { t: tErrors } = useTranslation('apiErrors');

  const userRightsAndLimitsTranslations: UserRightsAndLimitsCardTranslations = {
    mainTitle: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.mainTitle'),
    userRightsTitle: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.userRightsTitle'),
    userRightsText: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.userRightsText'),
    procurementLimitTitle: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.procurementLimitTitle'),
    procurementLimitInputLabel: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.procurementLimitInputLabel', {
      currency: user.organization?.locale.currency.toUpperCase() ?? '',
    }),
    procurementLimitOverrideCheckboxLabel: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.procurementLimitOverrideInherited_notUsedYet'),
    procurementLimitRequiredError: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.procurementLimitRequiredError'),
    procurementLimitNumberRequirdError: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.procurementLimitNumberRequirdError'),
    saveButton: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.saveButton'),
    cancelButton: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.cancelButton'),
    adminRoleName: _.capitalize(tGlobal('userRoles.admin')),
    userRoleName: _.capitalize(tGlobal('userRoles.user')),
    userLiteRoleName: _.capitalize(tGlobal('userRoles.user-lite')),
    approverRoleName: _.capitalize(tGlobal('userRoles.approver')),
    ordererRoleName: _.capitalize(tGlobal('userRoles.orderer')),
    allowAccessWithoutBankIdTitle: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.allowAccessWithoutBankIdTitle'),
    allowAccessWithoutBankIdText: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.allowAccessWithoutBankIdText'),
    allowAccessWithoutBankIdLabel: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.allowAccessWithoutBankIdLabel'),
    allowanceTitle: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.allowanceTitle'),
    allowanceExplanation: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.allowanceExplanation'),
    allowanceInputLabel: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.allowanceInputLabel', {
      currency: user.organization?.locale.currency.toUpperCase() ?? '',
    }),
    allowanceInputLabelOneTime: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.allowanceInputLabelOneTime', {
      currency: user.organization?.locale.currency.toUpperCase() ?? '',
    }),
    allowanceUnlimited: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.allowanceUnlimited'),
    allowanceEditLinkTo: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.allowanceEditLinkTo'),
    minBedriftLinktext: tUser('usersAdmin.userAdminPage.userRightsAndLimitsCard.minBedriftLinktext'),
    errorTitle: tUser('usersAdmin.userAdminPage.userSavingFailedErrorTitle'),
    errorMessage: tUser('usersAdmin.userAdminPage.userSavingFailedErrorMessage'),
  };

  const invoicingAccountCardTranslation: InvoicingAccountCardTranslations = {
    title: tUser('usersAdmin.userAdminPage.invoicingAccountCard.title'),
    overrideCheckboxLabel: tUser('usersAdmin.userAdminPage.invoicingAccountCard.overrideCheckboxLabel'),
    invoicingAccountDropdownLabel: tUser('usersAdmin.userAdminPage.invoicingAccountCard.invoicingAccountDropdownTitle'),
    accountNotValidTitle: tUser('usersAdmin.userAdminPage.invoicingAccountCard.accountNotValidTitle'),
    accountNotValidMessage: tUser('usersAdmin.userAdminPage.invoicingAccountCard.accountNotValidMessage'),
    userGroupAccountNotValidMessage: tUser('usersAdmin.userAdminPage.invoicingAccountCard.userGroupAccountNotValidMessage'),
    accountNotValidLabel: tUser('usersAdmin.userAdminPage.invoicingAccountCard.accountNotValidLabel'),
    saveButton: tUser('usersAdmin.userAdminPage.invoicingAccountCard.saveButton'),
    cancelButton: tUser('usersAdmin.userAdminPage.invoicingAccountCard.cancelButton'),
    noInvoicingAccountsInfo: tUser('usersAdmin.userAdminPage.invoicingAccountCard.noInvoicingAccountsInfo'),
    saveErrorTitle: tUser('usersAdmin.userAdminPage.invoicingAccountCard.saveErrorTitle'),
    saveErrorMessage: tUser('usersAdmin.userAdminPage.invoicingAccountCard.saveErrorMessage'),
  };

  const profileCardTranslations: ProfileCardTranslations = {
    title: tUser('usersAdmin.userAdminPage.ProfileCard.title'),
    nameRowTitle: tUser('usersAdmin.userAdminPage.ProfileCard.name'),
    groupRowTitle: tUser('usersAdmin.userAdminPage.ProfileCard.employeeGroup'),
    emailRowTitle: tUser('usersAdmin.userAdminPage.ProfileCard.email'),
    inactiveUserName: tUser('usersAdmin.userAdminPage.ProfileCard.inactiveUser'),
    employeeId: tUser('usersAdmin.userAdminPage.ProfileCard.employeeId'),
    preScheduledOnboardingTitle: tUser('usersAdmin.userAdminPage.ProfileCard.preScheduledOnboardingTitle'),
    preScheduledOnboardingDescription: tUser('usersAdmin.userAdminPage.ProfileCard.preScheduledOnboardingDescription'),
    companyDevicePolicyTitle: tUser('usersAdmin.userAdminPage.ProfileCard.companyDevicePolicyTitle'),
    companyDevicePolicyIsAccepted: tUser('usersAdmin.userAdminPage.ProfileCard.companyDevicePolicyIsAccepted'),
    companyDevicePolicyIsNotAccepted: tUser('usersAdmin.userAdminPage.ProfileCard.companyDevicePolicyIsNotAccepted'),
    companyDevicePolicyNotApplicable: tUser('usersAdmin.userAdminPage.ProfileCard.companyDevicePolicyNotApplicable'),
    phoneNumberRowTitle: tUser('usersAdmin.userAdminPage.ProfileCard.phoneNumber'),
    phoneNumberConfirmationNoticeTitle: tUser('usersAdmin.userAdminPage.ProfileCard.phoneNumberVerificationNotificationTitle'),
    phoneNumberConfirmationNoticeMessage: tUser('usersAdmin.userAdminPage.ProfileCard.phoneNumberVerificationNotificationMessage'),
    validPhoneNumberNotification: tUser('usersAdmin.userAdminPage.ProfileCard.validPhoneNumberNotification'),
    validCountryCodeNotification: tUser('usersAdmin.userAdminPage.ProfileCard.validCountryCodeNotification'),
    phoneNumberRequiredNotification: tUser('usersAdmin.userAdminPage.ProfileCard.phoneNumberRequiredNotification'),
    countryCodeRequiredNotification: tUser('usersAdmin.userAdminPage.ProfileCard.countryCodeRequiredNotification'),
    saveButton: tUser('usersAdmin.userAdminPage.ProfileCard.saveButton'),
    cancelButton: tUser('usersAdmin.userAdminPage.ProfileCard.cancelButton'),
    saveErrorTitle: tUser('usersAdmin.userAdminPage.userSavingFailedErrorTitle'),
    saveErrorMessage: tUser('usersAdmin.userAdminPage.userSavingFailedErrorMessage'),
    countries: {
      Finland: tGlobal('countries.Finland'),
      Sweden: tGlobal('countries.Sweden'),
      Norway: tGlobal('countries.Norway'),
      Denmark: tGlobal('countries.Denmark'),
      UnitedKingdom: tGlobal('countries.UnitedKingdom'),
    },
  };

  const assetsCardTranslations: AssetsCardTranslations = {
    title: tUser('usersAdmin.userAdminPage.assets.title'),
    noAssets: tUser('usersAdmin.userAdminPage.assets.noAssets'),
    endDate: tUser('usersAdmin.userAdminPage.assets.endDate'),
  };

  useEffect(() => {
    // Fetch all the backend data this component needs
    async function fetchData() {
      setLoading(true);
      setFetchUserError(false);
      try {
        // Asset Service tries to fetch invoicing accounts from Telia finance in the invoicing account request. It is possible that invoicing
        // account for user is removed, and by requesting user after the invoicing account request, invoicing account of the user has the
        // latest status (active=true|false).
        const invoicingAccounts = await getInvoicingAccounts();
        setOrganizationInvoicingAccounts(invoicingAccounts || []);
        const user = await getAUser(params.id, params.usergroupid);
        if (!user) {
          setFetchUserError(true);
          return;
        }
        setUser(user);
        const assets = await getUsersAllAssets(params.id);
        if (assets) setAssets(assets);
        else setFetchUserError(true);
      } catch {
        setFetchUserError(true);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [params]);

  const handleUserSave = async (userData: Partial<UpdateUserRequest>): Promise<boolean> => {
    const updateData: UpdateUserRequest = {
      phone_number: userData.phone_number || user.phone_number || '',
      user_roles: userData.user_roles || user.user_roles.user,
      allowed_procurements: user.allowed_procurements,
      policies: {
        lease_procurement_limit: user.policies?.lease_procurement_limit,
      },
      employee_id: userData.employee_id ?? user.employee_id,
      allow_access_without_bank_id: userData.allow_access_without_bank_id ?? user.allow_access_without_bank_id,
    };
    if (userData.policies?.lease_procurement_limit || userData.policies?.lease_procurement_limit === null) {
      updateData.policies!.lease_procurement_limit = userData.policies.lease_procurement_limit;
    }
    if (userData?.invoicing_account !== null) {
      updateData.invoicing_account = userData.invoicing_account || user.invoicing_account?.id;
    }

    const updatedUser = await updateUser(updateData, params.usergroupid, params.id);
    if (isAxiosError(updatedUser) || updatedUser === null) {
      switch (updatedUser?.response?.data?.error) {
        case ConflictErrorErrorEnum.EmailAlreadyInUse:
          profileCardTranslations.saveErrorMessage = tErrors('user.saveApiErrorMessageEmailInUse');
          break;
        case ConflictErrorErrorEnum.PhoneNumberAlreadyInUse:
          profileCardTranslations.saveErrorMessage = tErrors('user.saveApiErrorMessagePhoneNumberInUse');
          break;
        default:
          profileCardTranslations.saveErrorMessage = tUser('usersAdmin.userAdminPage.userSavingFailedErrorMessage');
          break;
      }
      return false;
    } else {
      setUser(prevUser => ({
        ...(updatedUser as User),
        user_group: prevUser.user_group,
      }));
      return true;
    }
  };

  const render = () => {
    let content;

    if (fetchUserError) {
      content = (
        <Div margin={{ top: 'xlg' }} alignTextToCenter>
          <Title color={'black'} size={'md'}>
            {tUser('usersAdmin.userAdminPage.userFetchFailedErrorMessage')}
          </Title>
        </Div>
      );
    } else if (loading) {
      content = (
        <Div margin={{ top: 'xlg' }} alignTextToCenter>
          <Loader />
        </Div>
      );
    } else {
      content = (
        <>
          <Headline size="md" margin={{ top: 'xlg' }} padding={{ top: 'md' }} data-testid="nameTitle">
            {user.name ? user.name : tUser('usersAdmin.userAdminPage.ProfileCard.inactiveUser')}
          </Headline>
          <Headline size="xs" margin={{ top: 'md', bottom: 'xlg' }} padding={{ bottom: 'sm' }}>
            {tUser('usersAdmin.userAdminPage.userSettings')}
          </Headline>
          <ProfileCard user={user} translations={profileCardTranslations} countryCallingCodes={COUNTRIES} submitValues={handleUserSave} />
          <Gutter />
          <InvoicingAccountCard
            userOrUserGroup={user}
            translations={invoicingAccountCardTranslation}
            invoicingAccounts={organizationInvoicingAccounts}
            submitValues={handleUserSave}
          />
          <Gutter />
          <UserRightsAndLimitsCard
            user={user}
            submitValues={handleUserSave}
            translations={userRightsAndLimitsTranslations}
            minBedriftLink={process.env.REACT_APP_MIN_BEDRIFT_LEGACY_URI ?? ''}
          />
          <Gutter />
          <AssetsCard assets={assets} getAssetLink={assetId => `/organizationassets/${assetId}`} translations={assetsCardTranslations} />
        </>
      );
    }

    return <Div style={{ maxWidth: 800 }}>{content}</Div>;
  };

  return render();
};

export default UserAdmin;
